// Фоновая задача на экране сопровождения (002: data-model.md, kernel.jobs; contracts/api.md,
// `Job`). Данные задачи (payload) наружу не отдаются (FR-047а).
import { z } from '../kernel/zod.ts';
import { ErrorCodeSchema } from '../errors/catalog.ts';
import { JobTypeSchema } from '../jobs/catalog.ts';
import { IdSchema, OperationIdSchema, TimestampSchema } from '../kernel/common.ts';

/** queued — ждёт, running — выполняется, done — выполнена, failed — упала, cancelled — отменена. */
export const jobStates = ['queued', 'running', 'done', 'failed', 'cancelled'] as const;
export type JobState = (typeof jobStates)[number];
export const JobStateSchema = z.enum(jobStates);

/** Состояние задачи словом — бейдж на экране (цвет дублируется словом). */
export const jobStateLabels = {
  queued: 'Ждёт',
  running: 'Выполняется',
  done: 'Выполнена',
  failed: 'Упала',
  cancelled: 'Отменена',
} as const satisfies Record<JobState, string>;

/** Первая строка ошибки задачи — не длиннее 300 знаков, без подробностей (FR-005а). */
export const JOB_ERROR_LIMIT = 300;

/**
 * Окно «Отменить» после «Повторить» (contracts/ui.md 002): тост живёт 10 секунд, а повторённая
 * задача ждёт столько же, прежде чем её возьмёт обработчик, — пока её не взяли, повтор отменяется.
 */
export const JOB_RETRY_UNDO_MS = 10_000;

export const JobSchema = z.object({
  id: IdSchema,
  type: JobTypeSchema,
  /** Подпись типа из каталога задач. */
  typeLabel: z.string(),
  state: JobStateSchema,
  /** Номер операции, в которой задачу поставили (FR-026). */
  operation: OperationIdSchema,
  attempts: z.number().int().nonnegative(),
  maxAttempts: z.number().int().positive(),
  /** Когда задача может выполняться: следующая попытка. */
  runAt: TimestampSchema,
  createdAt: TimestampSchema,
  startedAt: TimestampSchema.optional(),
  finishedAt: TimestampSchema.optional(),
  lastErrorCode: ErrorCodeSchema.optional(),
  lastError: z.string().max(JOB_ERROR_LIMIT).optional(),
  /** Для запуска по расписанию: имя расписания и наступившее время. */
  scheduleName: z.string().optional(),
  dueAt: TimestampSchema.optional(),
});
export type Job = z.infer<typeof JobSchema>;
